/**
 * Copyright Aquera Inc 2023
 *
 * This source code is licensed under the BSD-3-Clause license found in the
 * LICENSE file in the root directory of this source tree.
 */

import { css } from 'lit';

/**
 * MenuItem CSS
 */
export const styles = css`
  :host {
    box-sizing: border-box;
    -webkit-font-smoothing: var(--nile-webkit-font-smoothing, var(--ng-webkit-font-smoothing));
    -moz-osx-font-smoothing: var(--nile-moz-osx-font-smoothing, var(--ng-moz-osx-font-smoothing));
    text-rendering: var(--nile-text-rendering, var(--ng-text-rendering));
  }

  :host *,
  :host *::before,
  :host *::after {
    box-sizing: inherit;
  }

  [hidden] {
    display: none !important;
  }

  :host {
    display: block;
  }

  :host([inert]) {
    display: none;
  }

  .menu-item {
    position: relative;
    display: flex;
    align-items: stretch;
    font-size: var(--nile-font-size-rem-large ,var(--ng-font-size-text-sm));
    font-weight: var(--nile-font-weight-regular,var(--ng-font-weight-semibold));
    line-height: var(--nile-line-height-1-8,var(--ng-line-height-text-md));
    letter-spacing: normal;
    color: var(--nile-color-dark-1, var(--ng-colors-text-secondary-700));
    min-height:40px;
    transition: 150ms fill;
    user-select: none;
    white-space: nowrap;
    cursor: pointer;
    padding: var(--nile-spacing-none, var(--ng-spacing-md)) var(--nile-spacing-xl, var(--ng-spacing-xl));
  }

  .menu-item.menu-item--active {
    background-color: var(--nile-colors-primary-100, var(--ng-colors-bg-brand-primary));
    color: var(--nile-colors-primary-600, var(--ng-colors-bg-brand-solid));
  }

  .menu-item.menu-item--disabled {
    outline: none;
    opacity: 0.5;
    cursor: not-allowed;
  }

  .menu-item .menu-item__label {
    margin: auto var(--nile-spacing-none,var(--ng-spacing-none));
    display: inline-flex;
  }

  .menu-item .menu-item__prefix {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
  }

  .menu-item .menu-item__prefix::slotted(*) {
    margin-inline-end: var(--nile-font-size-rem-xsmall,var(--ng-spacing-md));
  }

  .menu-item .menu-item__suffix {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
  }

  .menu-item .menu-item__suffix::slotted(*) {
    margin-inline-start: var(--nile-font-size-rem-xsmall,var(--ng-spacing-md));
  }

  :host(:focus-visible) {
    outline: none;
  }

  :host(:hover:not([active], [aria-disabled='true'], :focus-visible)) .menu-item {
    background-color: var(--nile-colors-neutral-100,var(--ng-colors-bg-secondary-hover));
    color: var(--nile-colors-dark-900,var(--ng-colors-text-secondary-hover));
  }

  :host([active]:hover:not([aria-disabled='true'], :focus-visible)) .menu-item {
    background-color: var(--nile-colors-primary-100, var(--ng-colors-bg-brand-primary));
    color: var(--nile-colors-primary-600, var(--ng-colors-bg-brand-solid));
  }

  :host(:focus-visible) .menu-item {
    outline: none;
    background-color: var(--nile-colors-blue-500,var(--ng-colors-bg-active));
    color: var(--nile-colors-white-base,var(--ng-colors-text-primary-900));
    opacity: 1;
  }

  .menu-item .menu-item__check,
  .menu-item .menu-item__chevron {
    flex: 0 0 auto;
    display: flex;
    align-items: center;
    justify-content: center;
    width: 1.5em;
    visibility: hidden;
  }

  .menu-item--checked .menu-item__check,
  .menu-item--has-submenu .menu-item__chevron {
    visibility: visible;
  }

  @media (forced-colors: active) {
    :host(:hover:not([aria-disabled='true'])) .menu-item,
    :host(:focus-visible) .menu-item {
      outline: dashed 1px SelectedItem;
      outline-offset: -1px;
    }
  }
`;

export default [styles];
